import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import { Dropdown, positionDropdown } from '.'
import { getIsActiveFollowRoute } from '../../helpers/common'
import { CustomLink } from '../CustomLink'
import { HandleClickOutside } from '../HandleClickOutside'
import { LockAction } from '../LockAction'
import { Element } from '../html/element'
import { Icon } from '../html/icon'
import { DropdownList } from './DropdownList'
import classes from './index.module.scss'

const styles = {
  ACTIVE: {
    color: '#0553A5',
    backgroundColor: '#E5F2FF',
  },
  INACTIVE: {
    color: '#191D25',
    backgroundColor: '',
  },
}

export const DropdownItem = ({
  item,
  onClickItem,
  renderDropdownItem,
  childrenRef,
  appendItemStyle,
  activeItem,
}) => {
  const ref = useRef()
  const location = useLocation()

  const isActiveItem =
    Object.hasOwn(activeItem || {}, 'value') &&
    activeItem?.value === item?.value

  const [isShow, setIsShow] = useState(false)
  const [style, setStyle] = useState(
    item.isActive || isActiveItem ? styles.ACTIVE : styles.INACTIVE,
  )

  const onMouseEnter = () => {
    setStyle(styles.ACTIVE)
  }

  const onMouseLeave = () => {
    if (isActiveItem) return
    setStyle(styles.INACTIVE)
  }

  const renderItem = () => {
    return (
      <div className={classes.item}>
        <Element
          elementName="div"
          style={{
            color: item.disabled ? '#9C9C9C' : style.color,
            fontSize: 12,
            ...appendItemStyle,
          }}
        >
          {item.isI18n ? <Translate value={item.label} /> : item.label}&nbsp;
        </Element>
        {item.isLock && (
          <Icon
            className="icon-lock"
            style={{ color: '#9C9C9C', fontSize: 10, marginLeft: 5 }}
          />
        )}
        {item.children && (
          <Icon
            className="icon-caret-bottom"
            style={{ color: '#3A414B', fontSize: 8, marginLeft: 5 }}
          />
        )}
      </div>
    )
  }

  const renderContent = () => {
    return (
      <LockAction enable={item.isLock}>
        {item.renderIcon ? (
          <div className="dFlex alignItemsCenter">
            {item.renderIcon && (
              <div className={classes.icon}>{item.renderIcon()}</div>
            )}
            {renderItem()}
          </div>
        ) : (
          renderItem()
        )}
      </LockAction>
    )
  }

  const onClick = (e) => {
    if (item.disabled) return

    if (item.children) {
      setIsShow(!isShow)
    }

    onClickItem(item, e)
  }

  if (renderDropdownItem) {
    return renderDropdownItem(item, onClickItem)
  }

  return (
    <>
      <Element
        ref={ref}
        elementName="li"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        style={{
          backgroundColor: style.backgroundColor,
          cursor: !item.disabled && !item.isLock && 'pointer',
        }}
      >
        <CustomLink
          to={item.route}
          inActive={item.isLock}
          keepQueryStrParams={item.keepQueryStrParams}
        >
          {renderContent()}
        </CustomLink>
      </Element>

      {item.children && isShow && (
        <Dropdown
          parentRef={ref}
          position={positionDropdown.LEFT_START}
          dropdownSpace={2}
        >
          <HandleClickOutside
            exceptRef={ref}
            handleClickOutside={() => {
              setIsShow(false)
            }}
          >
            <div ref={childrenRef}>
              <DropdownList
                data={item.children.map((child) => {
                  return {
                    ...child,
                    isActive: getIsActiveFollowRoute(
                      location,
                      child.route,
                      child.children,
                    ),
                    isI18n: true,
                  }
                })}
              />
            </div>
          </HandleClickOutside>
        </Dropdown>
      )}
    </>
  )
}

DropdownItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.node,
    ]),
    route: PropTypes.string,
    isActive: PropTypes.bool,
    isI18n: PropTypes.bool,
    disabled: PropTypes.bool,
    renderIcon: PropTypes.func,
    isLock: PropTypes.bool,
    keepQueryStrParams: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.array,
  }).isRequired,
  onClickItem: PropTypes.func,
  renderDropdownItem: PropTypes.func,
  activeItem: PropTypes.object,
}

DropdownItem.defaultProps = {
  onClickItem: () => {},
}
